import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Oculta la etiqueta visualmente (sigue accesible para lectores de pantalla); usar con `placeholder`. */
  labelOculta?: boolean;
  error?: string | null;
  hint?: string;
  /** Adorno a la derecha del input (ej. boton de mostrar/ocultar contraseña). */
  trailing?: ReactNode;
}

export function TextField({
  label,
  labelOculta,
  error,
  hint,
  id,
  className,
  trailing,
  ...rest
}: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={labelOculta ? 'sr-only' : 'text-sm font-medium text-texto'}
        htmlFor={inputId}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          className={[
            'w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm text-texto placeholder:text-texto/40',
            'focus:outline-none focus:ring-2 focus:ring-navy/15',
            error ? 'border-red-300 focus:ring-red-200' : 'border-borde focus:border-navy/30',
            trailing && 'pr-10',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            [error && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
          }
          {...rest}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-1.5 flex items-center">{trailing}</div>
        )}
      </div>
      {hint && !error && (
        <span id={hintId} className="text-xs text-texto/50">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
